import { ReactNode, useMemo } from "react";
import prettyBytes from "pretty-bytes";

import { envTypes } from "types";
import { DurationString, getTimeDeltaString } from "utils";
import { LiveCounter } from "components/atoms";
import { QuestionMark } from "@mui/icons-material";

const EnvInfo = (
  env: envTypes.Environment & { mode: "teaser" | "details", reusables?: envTypes.ReusableEnvironment[] }
) => {
  const expireDate = useMemo(() => {
    if (!env?.createdAt || !env?.ttl || env.status === "empty") return null;
    const createdAt = new Date(env?.createdAt ?? "");
    return new Date(createdAt.getTime() + (env?.ttl ?? 0) * 60000);
  }, [env?.createdAt, env?.ttl, env?.status]);

  const textClasses = useMemo(() => {
    const baseClasses = "text-neutral";
    switch (env.mode) {
      case "teaser":
        return `${baseClasses} text-sm`;
      case "details":
        return `${baseClasses} text-m`;
    }
  }, [env.mode]);

  type InfoItem = { text: string | ReactNode; title?: string, help?: string };
  const infoItems = [] as InfoItem[];

  if (env?.status === "building" && env?.buildStartedAt)
    infoItems.push({
      text: (
        <LiveCounter
          startingTime={new Date(env.buildStartedAt)}
          pretext="for"
        />
      ),
      title: new Date(env?.buildStartedAt).toLocaleString(),
    });

  if (env?.buildTime)
    infoItems.push({
      text: `after ${DurationString(env?.buildTime / 1000)}`,
    });

  if (["built", "cancelled"].includes(env?.status) && env?.buildStartedAt)
    if (env?.buildTime) {
      // sum of build time and time since build started
      const buildFinishedAt = new Date(
        new Date(env?.buildStartedAt).getTime() + env?.buildTime
      );
      infoItems.push({
        text: ` ${getTimeDeltaString(buildFinishedAt)} ago`,
        title: "Completed at " + buildFinishedAt.toLocaleString(),
      });
    }
  if (env?.status === "failed" && env?.failedAt) {
    if (!env?.buildTime && env?.createdAt && env?.failedAt) {
      var duration =
        Math.abs(
          new Date(env?.failedAt).getTime() -
          new Date(env?.createdAt || "").getTime()
        ) / 1000;

      infoItems.push({
        text: `after ${DurationString(duration)}`,
      });
    }
    if (env?.failedAt)
      infoItems.push({
        text: `${getTimeDeltaString(new Date(env?.failedAt))} ago`,
        title: "Failed at " + new Date(env?.failedAt).toLocaleString(),
      });
  }

  if (expireDate)
    infoItems.push({
      text: `expires in ${getTimeDeltaString(expireDate)}`,
      title: "Expires on " + expireDate.toLocaleString(),
    });
  if (env?.size)
    infoItems.push({
      text: `occupies ${prettyBytes(env.size)}`,
      title: `${env.size} bytes (Volumes, if any, are not included)`,
    });

  if (env?.reusing) {
    const reusableEnv = env.reusables?.find(
      (reusable) => reusable.id === env.reusing
    );

    const sortedReusables = env.reusables?.filter((reusable) => reusable.label === reusableEnv?.label).sort((a, b) => a.savedAt < b.savedAt ? 1 : -1)
    console.log(sortedReusables)
    const latest = sortedReusables?.[0]?.id === reusableEnv?.id;

    if (reusableEnv)
      infoItems.push({
        text: `reusing ${reusableEnv.label}` + (!latest ? " (outdated)" : ""),
        title: `Reusable environment saved at ${new Date(reusableEnv.savedAt).toLocaleString()}`,
      });
  }

  if (env?.qaackFileSource === "repo")
    infoItems.push({
      text: `repo Qaackfile`,
      help: "https://docs.qaack.cloud/definitions/repository/#scheduled-rebuilds",
      title: `This environmnent has been generated from the Qaackfile located in the repository (on the associated git reference)`,
    });
  else
    infoItems.push({
      text: `panel Qaackfile`,
      help: "https://docs.qaack.cloud/definitions/repository/#scheduled-rebuilds",
      title: `This environmnent has been generated from the Qaackfile located in the repository settings`,
    });

  return (
    <>
      {infoItems.map((item, index) => (
        <p key={index} className={textClasses} title={item.title}>
          {item.text}
          {item.help &&
            <a href={item.help} target="_blank" rel="noreferrer" title="Help">
              <QuestionMark fontSize="inherit" className={textClasses} />
            </a>
          }
          {index < infoItems.length - 1 && ","}
        </p>
      ))}
    </>
  );
};

export default EnvInfo;
